import { useNavigate } from "react-router-dom";
import { useBrain } from "@/contexts/ui/brain";
import { getLearnTopic } from "@/lib/learn/learnTopicRegistry";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface LearnMoreButtonProps {
  topic: string;
  /** Optional override label for the trigger button. */
  label?: string;
  /** Compact icon-only variant for dense headers. */
  iconOnly?: boolean;
  /** "link" renders a quiet inline text link instead of a bordered button. */
  variant?: "button" | "link";
  className?: string;
}

const gradientBorderStyle: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(#ffffff, #ffffff), linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
  backgroundOrigin: "border-box",
  backgroundClip: "padding-box, border-box",
};

const LearnMoreButton = ({ topic, label, iconOnly, variant = "button", className }: LearnMoreButtonProps) => {
  const navigate = useNavigate();
  const { openBrain } = useBrain();
  const config = getLearnTopic(topic);
  if (!config) return null;

  const triggerLabel = label || config.label || "Learn more";

  const handleOpenArticle = () => navigate(`/learn/articles/${config.articleSlug}`);
  const handleAskTutor = () => openBrain(config.widgetPrompt);

  return (
    <Popover>
      <PopoverTrigger asChild>
        {variant === "link" ? (
          <button
            type="button"
            className={cn(
              "inline-flex items-center gap-1.5 text-sm font-medium text-genie-primary",
              "underline-offset-4 transition-colors hover:underline",
              className,
            )}
          >
            <GenieIcon
              name="lightbulb"
              size={16}
              className="h-4 w-4"
            />
            {triggerLabel}
          </button>
        ) : (
          <button
            type="button"
            aria-label={iconOnly ? triggerLabel : undefined}
            className={cn(
              "inline-flex items-center justify-center gap-2 rounded-xl text-sm font-semibold text-[#222B45]",
              "border-2 border-transparent shadow-sm transition-all duration-200",
              "hover:shadow-md hover:-translate-y-0.5",
              iconOnly ? "h-10 w-10" : "px-4 py-2",
              className,
            )}
            style={gradientBorderStyle}
          >
            <GenieIcon
              name="lightbulb"
              size={20}
              className="h-5 w-5"
            />
            {!iconOnly && triggerLabel}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 p-0 rounded-2xl border-0 shadow-xl overflow-hidden"
      >
        <div
          className="p-[2px] rounded-2xl"
          style={{
            background:
              "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)",
          }}
        >
          <div className="bg-white rounded-[14px] p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-genie-primary/10 to-genie-secondary/10">
                <GenieIcon
                  name="lightbulb"
                  size={24}
                  className="h-6 w-6"
                />
              </div>
              <div>
                <div className="text-base font-semibold text-[#222B45] leading-tight">
                  Get up to speed
                </div>
                <div className="text-xs text-[#64748B] mt-0.5">
                  Read the guide or ask your Genie
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={handleOpenArticle}
                className="inline-flex items-center gap-2.5 w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md shadow-genie-primary/30 hover:shadow-lg hover:shadow-genie-primary/40 hover:scale-[1.02] transition-all duration-200"
                style={{
                  background:
                    "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
                }}
              >
                <GenieIcon
                  name="book-open"
                  size={20}
                  className="h-5 w-5 brightness-0 invert"
                />
                View guide
              </button>
              <button
                type="button"
                onClick={handleAskTutor}
                className="inline-flex items-center gap-2.5 w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-[#222B45] bg-white border border-[#E5EAF0] hover:border-genie-primary/40 hover:bg-gray-50 transition-all duration-200"
              >
                <GenieIcon
                  name="chat"
                  size={20}
                  className="h-5 w-5"
                />
                Ask your Genie
              </button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default LearnMoreButton;
